import React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toISODate } from "@/lib/format";

function isoToUK(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

// Parse typed text into an ISO date. Accepts dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy, 2 or 4 digit year.
function ukToISO(text) {
  const parts = String(text).trim().split(/[\/\-.]/);
  if (parts.length !== 3) return null;
  let [d, m, y] = parts.map((p) => p.trim());
  if (!d || !m || !y) return null;
  d = parseInt(d, 10);
  m = parseInt(m, 10);
  y = parseInt(y, 10);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
  if (y < 100) y += 2000;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return toISODate(dt);
}

export default function DatePicker({ value, onChange, placeholder = "DD/MM/YYYY", testId }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(isoToUK(value));

  React.useEffect(() => {
    setText(isoToUK(value));
  }, [value]);

  const dateObj = value ? new Date(value + "T00:00:00") : undefined;

  const commitText = (raw) => {
    if (raw.trim() === "") {
      onChange(null);
      return;
    }
    const iso = ukToISO(raw);
    if (iso) onChange(iso);
    else setText(isoToUK(value)); // revert invalid entry
  };

  return (
    <div className="relative">
      <Input
        value={text}
        data-testid={testId}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitText(e.target.value);
          }
        }}
        className="h-10 border-cass-border pr-10 tabular focus-visible:ring-cass-blue"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid={testId ? `${testId}-calendar` : undefined}
            className="absolute right-0 top-0 h-10 w-10 text-cass-blue hover:bg-transparent"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="end">
          <Calendar
            mode="single"
            selected={dateObj}
            onSelect={(d) => {
              onChange(d ? toISODate(d) : null);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
